import axios from 'axios'
import { uploadFileToOss } from '@/utils/directOssUpload'
import { ElMessage } from 'element-plus'
import router from '@/router'
import { clearCache } from '@/utils/clearCache'

// 登录失效锁
let isLoginExpired = false

// ✅ 核心：根据当前域名获取对应的baseURL
export const getBaseUrl = () => {
  // 1. 测试环境 → 双域名切换逻辑
  if (process.env.VUE_APP_ENV === 'test') {
    // const host = window.location.hostname
    // const testDomain1 = process.env.VUE_APP_TEST_DOMAIN1
    // const testDomain2 = process.env.VUE_APP_TEST_DOMAIN2
    // const testApi1 = process.env.VUE_APP_TEST_API1
    // const testApi2 = process.env.VUE_APP_TEST_API2

    // if (host === testDomain1) return testApi1
    // if (host === testDomain2) return testApi2
    // 测试环境兜底
    return process.env.VUE_APP_BASE_API
  }

  // 2. 正式服 → 原有逻辑保持不变
  if (process.env.VUE_APP_ENV === 'production') {
    const host = window.location.hostname
    const domain1 = process.env.VUE_APP_PROD_DOMAIN1
    const domain2 = process.env.VUE_APP_PROD_DOMAIN2
    const api1 = process.env.VUE_APP_PROD_API1
    const api2 = process.env.VUE_APP_PROD_API2

    if (host === domain1) return api1
    if (host === domain2) return api2
    // 正式服兜底
    return api1
  }

  // 其他环境（如本地开发）→ 兜底
  return process.env.VUE_APP_BASE_API
}

// 创建 axios 实例（动态设置baseURL）
const service = axios.create({
  baseURL: getBaseUrl(), // 替换原来的固定baseURL
  timeout: 1000000
})

// 以下原有代码保持不变 ↓
// 请求拦截器（如果需要可以解开注释）
// service.interceptors.request.use(
//   config => {
//     const token = localStorage.getItem('token')
//     if (token) {
//       config.headers['Authorization'] = 'Bearer ' + token
//     }
//     return config
//   },
//   error => {
//     return Promise.reject(error)
//   }
// )

// 响应拦截器（修复：不拦截 blob 下载请求）
// Keep existing upload callers unchanged while moving bytes to direct OSS upload.
service.interceptors.request.use(async (config) => {
  const requestUrl = String(config.url || '').split('?')[0]
  const isDirectUploadEndpoint = requestUrl === '/ai/upload' || requestUrl === '/ai/uploadVideo'
  const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData
  if (!isDirectUploadEndpoint || !isFormData || config.skipDirectOssUpload) return config

  const file = config.data.get('file')
  const responseData = await uploadFileToOss(service, file, {
    isVideo: requestUrl === '/ai/uploadVideo',
    onUploadProgress: config.onUploadProgress
  })

  // Return the original endpoint response shape so page-level code needs no changes.
  return {
    ...config,
    adapter: async (adapterConfig) => ({
      data: responseData,
      status: 200,
      statusText: 'OK',
      headers: {},
      config: adapterConfig,
      request: { responseType: adapterConfig.responseType }
    })
  }
}, (error) => Promise.reject(error))

service.interceptors.response.use(
  (response) => {
    // ✅ 关键修复：如果是下载 blob，直接返回数据，不走业务 code 判断
    if (response.request.responseType === 'blob') {
      return response
    }

    const res = response.data || {}
    const code = res.code

    // 成功
    if (code === 0) {
      return response
    }

    // 登录失效
    if (code === 30007) {
      if (isLoginExpired) return new Promise(() => {})

      isLoginExpired = true
      ElMessage.error('您还未登录或登录失效，请重新登录！')
      
      clearCache.clearAllCache()
      localStorage.removeItem('token')
      router.push('/auth').finally(() => {
        setTimeout(() => (isLoginExpired = false), 1000)
      })
      return new Promise(() => {})
    }

    // 其他错误
    ElMessage.error(res.msg || '请求失败')
    return Promise.reject(res)
  },
  (error) => {
    console.error('请求异常：', error)
    if (error.isDirectOssUploadError) {
      ElMessage.error(error.message)
      return Promise.reject(error)
    }
    ElMessage.error('系统升级中，请稍等')
    return Promise.reject(error)
  }
)

// 路由切换重置锁
router.afterEach(() => {
  setTimeout(() => (isLoginExpired = false), 500)
})

// ✅ 修复：下载函数放在导出前
// 文件下载方法（POST / 流下载）
export const getDownload = (url, params, fileName = 'file') => {
  return service({
    method: 'post',
    url,
    params,
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    responseType: 'blob'
  }).then(response => {
    const blob = new Blob([response.data])
    const downloadUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = downloadUrl

    // 尝试从响应头获取真实文件名
    const disposition = response.headers['content-disposition']
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (match && match[1]) {
        fileName = decodeURIComponent(match[1].replace(/['"]/g, ''))
      }
    }

    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(downloadUrl)
  }).catch(err => {
    ElMessage.error('下载失败')
    console.error('下载错误', err)
  })
}

export default service
